// /utils/formatting.ts

export function formatMileage(
    mileage: number,
    metric: boolean,
    locale: string = "en-US",
    showUnit: boolean = true,
    useThousandSeparator: boolean = true,
): string | number {
    const formatNumber = (value: number) =>
        useThousandSeparator
            ? formatNumberWithThousandsSeparator(value)
            : value.toString();

    if (metric) {
        const km = mileage * 1.60934;
        const kmValue = parseFloat(km.toFixed(0));
        const formattedKm = formatNumber(kmValue);

        if (!showUnit) return kmValue;
        return `${formattedKm} km`;
    } else {
        const formattedMiles = formatNumber(mileage);

        if (!showUnit) return mileage;
        const unit = mileage === 1 ? "mile" : "miles";
        return `${formattedMiles} ${unit}`;
    }
}

export function formatDurationMonths(months: number): string {
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    let result = "";

    if (years > 0) {
        result += `${years} ${years === 1 ? "year" : "years"}`;
    }

    if (remainingMonths > 0 || (years === 0 && remainingMonths === 0)) {
        if (years > 0) result += " ";
        result += `${remainingMonths} ${remainingMonths === 1 ? "month" : "months"}`;
    }

    return result;
}

export function formatTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0 && remainingMinutes > 0) {
        return `${hours} h ${Math.floor(remainingMinutes)} min`;
    } else if (hours > 0) {
        return `${hours} h`;
    }
    return `${Math.floor(remainingMinutes)} min`;
}

export function formatNumberWithThousandsSeparator(
    num: number,
    locale: string = "en-US",
): string {
    if (num % 1 === 0) {
        return num.toLocaleString(locale, {maximumFractionDigits: 0});
    } else {
        return num.toLocaleString(locale, {maximumFractionDigits: 2});
    }
}

export function formatPence(pence: number, locale: string = "en-US"): string {
    pence = pence / 100;
    return formatNumberWithThousandsSeparator(pence, locale);
}

export const formatVrn = (vrn: string): string => {
    if (vrn.length >= 7) {
        const output = [vrn.slice(0, 4), " ", vrn.slice(4)].join("").toUpperCase();
        return output;
    }
    return vrn.toUpperCase();
};

export function formatTitleCase(str: string) {
    return str.replace(
        /\w\S*/g,
        (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase(),
    );
}

export function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    const day: number = date.getDate();
    const monthIndex: number = date.getMonth();
    const year: number = date.getFullYear();

    const getOrdinal = (day: number): string => {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
            case 1:
                return 'st';
            case 2:
                return 'nd';
            case 3:
                return 'rd';
            default:
                return 'th';
        }
    };

    const ordinal: string = getOrdinal(day);

    const months: string[] = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month: string = months[monthIndex];

    return `${day}${ordinal} ${month} ${year}`;
}

export function formatDateSimple(dateStr: string): string {
    const date = new Date(dateStr);
    const day: number = date.getDate();
    const monthIndex: number = date.getMonth();
    const year: number = date.getFullYear();

    const months: string[] = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month: string = months[monthIndex];

    return `${day} ${month} ${year}`;
}